import { useState, useCallback } from "react";
import { toast } from "sonner";
import { logError } from "@/lib/logger";

// PDF export + browser Print + CSV download for the Connections page,
// mirroring the report export hook but scoped to the off-screen ConnectionsPrint node.
export function useConnectionsExport(printRef, contacts) {
  const [exporting, setExporting] = useState(false);
  const [printing, setPrinting] = useState(false);

  const hasData = Array.isArray(contacts) && contacts.length > 0;

  const exportPdf = useCallback(async () => {
    if (!hasData) {
      toast.error("No contacts to export yet.");
      return;
    }
    setExporting(true);
    try {
      const html2pdf = (await import("html2pdf.js")).default;
      await new Promise((r) => setTimeout(r, 400));
      const opt = {
        margin: [10, 10, 10, 10],
        filename: `salestap-connections-${new Date().toISOString().slice(0, 10)}.pdf`,
        image: { type: "jpeg", quality: 0.95 },
        html2canvas: { scale: 2, useCORS: true, backgroundColor: "#ffffff", scrollX: 0, scrollY: 0, windowWidth: document.documentElement.offsetWidth },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        pagebreak: { mode: ["avoid-all", "css", "legacy"] },
      };
      await html2pdf().set(opt).from(printRef.current).save();
      toast.success("PDF exported.");
    } catch (e) {
      logError("Connections PDF export failed:", e);
      toast.error("PDF export failed.");
    } finally {
      setExporting(false);
    }
  }, [printRef, hasData]);

  const exportCsv = useCallback(() => {
    if (!hasData) {
      toast.error("No contacts to export yet.");
      return;
    }
    try {
      const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
      const header = ["Name", "Company", "Phone", "Email"];
      const rows = contacts.map((c) => [c.name, c.company, c.phone, c.email].map(esc).join(","));
      const csv = [header.join(","), ...rows].join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `salestap-contacts-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("CSV exported.");
    } catch (e) {
      logError("Connections CSV export failed:", e);
      toast.error("CSV export failed.");
    }
  }, [contacts, hasData]);

  const printPage = useCallback(async () => {
    if (!hasData) {
      toast.error("No contacts to print yet.");
      return;
    }
    setPrinting(true);
    try {
      await new Promise((r) => setTimeout(r, 300));
      const el = printRef.current;
      if (!el) throw new Error("Nothing to print");
      const clone = el.cloneNode(true);
      clone.style.position = "static";
      clone.style.left = "0";
      clone.style.top = "0";
      clone.style.width = "100%";
      const iframe = document.createElement("iframe");
      iframe.style.position = "fixed";
      iframe.style.right = "0";
      iframe.style.bottom = "0";
      iframe.style.width = "0";
      iframe.style.height = "0";
      iframe.style.border = "0";
      document.body.appendChild(iframe);
      const doc = iframe.contentWindow.document;
      doc.open();
      doc.write(
        `<!DOCTYPE html><html><head><title>SalesTap Connections</title><meta charset="utf-8" />` +
          `<style>@page{margin:12mm;} body{margin:0;font-family:Arial,Helvetica,sans-serif;-webkit-print-color-adjust:exact;print-color-adjust:exact;} table{border-collapse:collapse;}</style>` +
          `</head><body></body></html>`
      );
      doc.close();
      doc.body.appendChild(doc.importNode(clone, true));
      await new Promise((r) => setTimeout(r, 350));
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
      setTimeout(() => document.body.removeChild(iframe), 1000);
      toast.success("Opening print dialog…");
    } catch (e) {
      logError("Connections print failed:", e);
      toast.error("Print failed.");
    } finally {
      setPrinting(false);
    }
  }, [printRef, hasData]);

  return { exporting, exportPdf, exportCsv, printing, printPage };
}
